import React, { useState, useEffect } from 'react';
import apiClient, { STATIC_CONFIG } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Card,
  CardContent,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Chip,
  Checkbox,
  IconButton,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  ContactEmergency as ContactEmergencyIcon,
  Recommend as RecommendIcon,
  Delete as DeleteIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import MultiLoginDialog from '../common/MultiLoginDialog';

// 空数据模板
const emptyData = {
  // 个人基本信息
  personalInfo: {
    fullName: '',
    gender: '',
    dateOfBirth: '',
    nationality: '',
    passportNumber: '',
    idCardNumber: '',
    maritalStatus: '',
    religion: '',
    passportIssueDate: '',
    passportExpiryDate: '',
    passportIssuePlace: '',
    phone: '',
    email: '',
    bodyHeight: '',
    bodyWeight: '',
    salary: '',
    siblingsCount: '',
    siblingsRanking: '',
    ifCee: '',
    motherName: '',
    fatherName: '',
    fatherSalary: '',
    CorrespondenceAddress: '',
    PermanentAddress: ''
  },
  //英语语言能力
  englishLanguage: {
    languageType: '',
    languageCustomType: '',
    languageResult: ''
  },
  // 教育背景
  education: [],
  //工作经历
  workExperience: [],
  // 联系人信息
  emergencyContact: [],
  // 推荐人信息
  referee: [],
  // 申请信息
  additionalInformation: {
    preferredStartDate: '',
    studyBudget: '',
    accommodationNeeds: '',
    visaHistory: '',
    additionalInfo: '',
  },
};
// formData转换
const parseJSONFields = (data) => {
  if (!data || typeof data !== 'object') return {};
  const keysToParse = [
    'personalInfo',
    'englishLanguage',
    'education',
    'workExperience',
    'emergencyContact',
    'referee',
    'selectedPackage',
    'additionalInformation',
  ];

  const result = {};

  for (const key of keysToParse) {
    if (key in data) {
      const value = data[key];
      if (typeof value === 'string') {
        try {
          result[key] = JSON.parse(value);
        } catch {
          result[key] = value; // 保留原始字符串
        }
      } else {
        result[key] = value; // 非字符串，直接保留
      }
    }
  }
  return result;
};

// 判断formData是否为空
const isFormDataEmpty = (formData) => {
  if (!formData) return true;
  const keys = ['personalInfo', 'englishLanguage', 'education', 'workExperience', 'emergencyContact', 'referee', 'additionalInformation'];
  return keys.every(key => {
    const value = formData[key];
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return !value || Object.keys(value).length === 0;
    return !value;
  });
};

// 智能默认值处理函数
const getSafeValue = (value, defaultValue = '') => {
  return value !== undefined && value !== null ? value : defaultValue;
};


const ApplicationForm = ({ 
  selectedProgram, 
  selectedPackage, 
  onFormDataUpdate, 
  onNext, 
  onBack, 
  formData = {},
  onRequireLogin,
  selectedLevel
}) => {
  const [currentFormStep, setCurrentFormStep] = useState(0);
  const [data, setData] = useState(() => {
    const parsedFormData = parseJSONFields(formData);
    const initialData = isFormDataEmpty(parsedFormData) ? emptyData : parsedFormData;
    return {
      ...emptyData,
      ...initialData
    };
  });


  const fieldLabels = {
    // personalInfo
    fullName: '姓名',
    gender: '性别',
    dateOfBirth: '出生日期',
    nationality: '国籍',
    passportNumber: '护照号码',
    idCardNumber: '身份证号',
    maritalStatus: '婚姻状况',
    religion: '宗教信仰',
    passportIssueDate: '护照签发日期',
    passportExpiryDate: '护照到期日期',
    passportIssuePlace: '护照签发地',
    phone: '手机号码',
    email: '电子邮箱',
    bodyHeight: '身高',
    bodyWeight: '体重',
    salary: '月薪',
    siblingsCount: '兄弟姐妹数',
    siblingsRanking: '在兄弟姐妹排行',
    ifCee: '是否参加高考',
    motherName: '母亲姓名',
    fatherName: '父亲姓名',
    fatherSalary: '父亲月薪',
    CorrespondenceAddress: '通信地址',
    PermanentAddress: '永久地址',
  
    // englishLanguage
    languageType: '语言考试类型',
    languageCustomType: '其他语言类型',
    languageResult: '语言成绩',
  
    // education
    currentEducationLevel: '当前教育程度',
    schoolName: '学校名称',
    major: '专业',
    gpa: '绩点',
    enrolledDate: '入学时间',
    graduationDate: '毕业时间',
    studyMode: '学习模式',
  
    // workExperience
    companyName: '公司名称',
    position: '职位',
    startDate: '开始日期',
    endDate: '结束日期',
    isCurrent: '至今仍在职',
    address: '地址',
    postalCode: '邮政编码',
    workType: '工作类型',
    description: '工作内容',
  
    // emergencyContact & referee
    name: '姓名',
    birthDate: '出生日期',
    relation: '与申请人关系',
    companyPhone: '单位电话',
    homePhone: '家庭电话',
    mobilePhone: '手机号码',
    idNumber: '身份证号',
    passportNumber: '护照号',
  
    // referee 专属
    email: '电子邮箱',
    companyAddress: '工作单位地址',
  
    // application
    preferredStartDate: '期望入学时间',
    studyBudget: '学习预算',
    accommodationNeeds: '住宿需求',
    visaHistory: '签证记录',
    workExperience: '工作经历说明',
    motivation: '学习动机',
    careerGoals: '职业目标',
    additionalInfo: '补充信息',
  };
  

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const { isLoggedIn: isAuthenticated } = useUser();

  const formSteps = [
    {
      title: '个人基本信息',
      icon: <PersonIcon />,
      description: '请填写您的个人基本信息'
    },
    {
      title: '英语语言信息',
      icon: <LanguageIcon />,
      description: '请填写您的英语语言信息'
    },
    {
      title: '教育背景',
      icon: <SchoolIcon />,
      description: '请提供您的教育背景信息'
    },
    {
      title: '工作经历',
      icon: <WorkIcon />,
      description: '请提供您的工作经历'
    },
    {
      title: '联系人信息',
      icon: <ContactEmergencyIcon />,
      description: '请提供您的联系人信息'
    },
    {
      title: '推荐人信息',
      icon: <RecommendIcon />,
      description: '请提供您的推荐人信息'
    },
    {
      title: '其他信息',
      icon: <InfoOutlinedIcon />,
      description: '请填写其他信息'
    },
    {
      title: '确认信息',
      icon: <ConfirmationNumberIcon />,
      description: '请确认所有信息无误'
    }
  ];



  const handleInputChange = (section, field, value) => {
    setData(prev => {
      const newData = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      };
      if (onFormDataUpdate) onFormDataUpdate({
        personalInfo: newData.personalInfo,
        education: newData.education,
        workExperience: newData.workExperience,
        emergencyContact: newData.emergencyContact,
        referee: newData.referee,
        additionalInformation: newData.additionalInformation,
        englishLanguage: newData.englishLanguage,
        selectedPackage: newData.selectedPackage,
      });
      return newData;
    });
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    if (currentFormStep === 0) {
      // 验证个人基本信息
      if (!data.personalInfo.fullName) newErrors['personalInfo.fullName'] = '请输入姓名';
      if (!data.personalInfo.gender) newErrors['personalInfo.gender'] = '请选择性别';
      if (!data.personalInfo.dateOfBirth) newErrors['personalInfo.dateOfBirth'] = '请输入出生日期';
      if (!data.personalInfo.nationality) newErrors['personalInfo.nationality'] = '请输入国籍';
      if (!data.personalInfo.passportNumber) newErrors['personalInfo.passportNumber'] = '请输入护照号码';
      if (!data.personalInfo.phone) newErrors['personalInfo.phone'] = '请输入手机号码';
      if (!data.personalInfo.email) newErrors['personalInfo.email'] = '请输入邮箱地址';
    } else if (currentFormStep === 1) {
      //
      if (!data.englishLanguage.languageType) newErrors['englishLanguage.languageType'] = '请选择英语语言能力';
      if (data.englishLanguage.languageType === 'OTHERS' && !data.englishLanguage.languageCustomType) newErrors['englishLanguage.languageCustomType'] = '请填写英语语言能力';
      if (!data.englishLanguage.languageResult) newErrors['englishLanguage.languageResult'] = '请填写英语语言成绩';
    }else if (currentFormStep === 2) {
      // 验证教育背景
      if (Array.isArray(data.education) && data.education.length > 0) {
        data.education.forEach((edu, index) => {
          if (!edu.currentEducationLevel) newErrors[`education.${index}.currentEducationLevel`] = '请选择当前教育水平';
          if (!edu.schoolName) newErrors[`education.${index}.schoolName`] = '请输入学校名称';
          if (!edu.enrolledDate) newErrors[`education.${index}.enrolledDate`] = '请选择入学时间';
          if (!edu.graduationDate) newErrors[`education.${index}.graduationDate`] = '请选择毕业时间';
        });
      } else {
        newErrors['education'] = '请至少添加一个教育经历';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormNext = async () => {
    if (validateCurrentStep()) {
      if (currentFormStep < formSteps.length - 1) {
        setCurrentFormStep(currentFormStep + 1);
      } else {
        // 最后一步，提交表单
        setLoading(true);
        setSubmitError(null);
        try {
          // 合并 selectedProgram 和 selectedPackage 到提交数据
          const submitData = {
            ...data,
            selectedProgram: selectedProgram?.id || selectedProgram, // 只传 id
            selectedPackage,
            selectedLevel,
          };
          // 使用apiClient提交API
          const result = await apiClient.post('/api/applications', submitData);
        if (result.status === 401 || result.status === 403 || (result.data && (result.data.message?.includes('未认证') || result.data.error?.includes('未认证')))) {
            // 清除登录状态
            localStorage.removeItem('auth_token');
            // 触发状态更新事件
            window.dispatchEvent(new CustomEvent('authTokenCleared'));
            handleRequireLogin();
            return;
        }
          if (!result.success && (result.message?.includes('未认证') || result.error?.includes('未认证'))) {
            // 清除登录状态
            localStorage.removeItem('auth_token');
            // 触发状态更新事件
            window.dispatchEvent(new CustomEvent('authTokenCleared'));
            handleRequireLogin();
            return;
          }
          if (!result.success) throw new Error(result.message || '提交失败');
          if (onNext) {
            onNext(result.data); // 可传递新建的申请数据
          }
        } catch (err) {
          // 检查是否未认证
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            // 清除登录状态
            localStorage.removeItem('auth_token');
            // 触发状态更新事件
            window.dispatchEvent(new CustomEvent('authTokenCleared'));
            handleRequireLogin();
            return;
        }
          if (err.response && (err.response.data?.message?.includes('未认证') || err.response.data?.error?.includes('未认证'))) {
            // 清除登录状态
            localStorage.removeItem('auth_token');
            // 触发状态更新事件
            window.dispatchEvent(new CustomEvent('authTokenCleared'));
            handleRequireLogin();
            return;
          }
          setSubmitError(err.message || '提交失败');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleFormBack = () => {
    if (currentFormStep > 0) {
      setCurrentFormStep(currentFormStep - 1);
    } else {
      if (onBack) {
        onBack();
      }
    }
  };
  // 工作经历增删改
  const handleExperienceChange = (index, field, value) => {
    setData(prev => {
      const updated = prev.workExperience.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      );
      const newData = { ...prev, workExperience: updated };
      if (onFormDataUpdate) onFormDataUpdate({
        personalInfo: newData.personalInfo,
        education: newData.education,
        workExperience: newData.workExperience,
        emergencyContact: newData.emergencyContact,
        referee: newData.referee,
        additionalInformation: newData.additionalInformation,
        englishLanguage: newData.englishLanguage,
        selectedPackage: newData.selectedPackage,
      });
      return newData;
    });
  };
  
  const handleAddExperience = () => {
    const newExperience = {
      companyName: '',
      position: '',
      startDate: '',
      endDate: '',
      isCurrent: false,
      address: '',
      postalCode: '',
      phone: '',
      workType: '',
      description: ''
    };
    const updated = [...data.workExperience, newExperience];
    const newData = { ...data, workExperience: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };
  
  const handleDeleteExperience = (index) => {
    const updated = data.workExperience.filter((_, i) => i !== index);
    const newData = { ...data, workExperience: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  //
  // 增删改操作函数，放到组件里
  const handleContactChange = (index, field, value) => {
    setData(prev => {
      const updated = prev.emergencyContact.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      );
      const newData = { ...prev, emergencyContact: updated };
      if (onFormDataUpdate) onFormDataUpdate({
        personalInfo: newData.personalInfo,
        education: newData.education,
        workExperience: newData.workExperience,
        emergencyContact: newData.emergencyContact,
        referee: newData.referee,
        additionalInformation: newData.additionalInformation,
        englishLanguage: newData.englishLanguage,
        selectedPackage: newData.selectedPackage,
      });
      return newData;
    });
  };

  const handleAddContact = () => {
    if (data.emergencyContact.length >= 2) return; // 最多2个联系人
    const newContact = {
      name: '',
      birthDate: '',
      relation: '',
      companyName: '',
      position: '',
      address: '',
      postalCode: '',
      homePhone: '',
      companyPhone: '',
      mobilePhone: '',
      salary: '',
      idNumber: '',
      passportNumber: '',
    };
    const updated = [...data.emergencyContact, newContact];
    const newData = { ...data, emergencyContact: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  const handleDeleteContact = (index) => {
    const updated = data.emergencyContact.filter((_, i) => i !== index);
    const newData = { ...data, emergencyContact: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  // 推荐人增删改
  const handleRefereeChange = (index, field, value) => {
    setData(prev => {
      const updated = prev.referee.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      );
      const newData = { ...prev, referee: updated };
      if (onFormDataUpdate) onFormDataUpdate({
        personalInfo: newData.personalInfo,
        education: newData.education,
        workExperience: newData.workExperience,
        emergencyContact: newData.emergencyContact,
        referee: newData.referee,
        additionalInformation: newData.additionalInformation,
        englishLanguage: newData.englishLanguage,
        selectedPackage: newData.selectedPackage,
      });
      return newData;
    });
  };

  const handleAddReferee = () => {
    if (data.referee.length >= 2) return;
    const newReferee = {
      name: '',
      phone: '',
      email: '',
      companyName: '',
      position: '',
      companyAddress: '',
      postalCode: '',
    };
    const updated = [...data.referee, newReferee];
    const newData = { ...data, referee: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  const handleDeleteReferee = (index) => {
    const updated = data.referee.filter((_, i) => i !== index);
    const newData = { ...data, referee: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  // 教育经历增删改
  const handleEducationChange = (index, field, value) => {
    setData(prev => {
      const updated = prev.education.map((edu, i) =>
        i === index ? { ...edu, [field]: value } : edu
      );
      const newData = { ...prev, education: updated };
      if (onFormDataUpdate) onFormDataUpdate({
        personalInfo: newData.personalInfo,
        education: newData.education,
        workExperience: newData.workExperience,
        emergencyContact: newData.emergencyContact,
        referee: newData.referee,
        additionalInformation: newData.additionalInformation,
        englishLanguage: newData.englishLanguage,
        selectedPackage: newData.selectedPackage,
      });
      return newData;
    });
  };

  const handleAddEducation = () => {
    const newEducation = {
      currentEducationLevel: '',
      schoolName: '',
      major: '',
      gpa: '',
      enrolledDate: '',
      graduationDate: '',
      studyMode: ''
    };
    const updated = [...data.education, newEducation];
    const newData = { ...data, education: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  const handleDeleteEducation = (index) => {
    const updated = data.education.filter((_, i) => i !== index);
    const newData = { ...data, education: updated };
    setData(newData);
    onFormDataUpdate?.(newData);
  };

  const handleRequireLogin = () => setLoginOpen(true);
  const handleLoginSuccess = () => {
      setLoginOpen(false);
  };


  const renderPersonalInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="姓名 *"
          value={getSafeValue(data.personalInfo.fullName)}
          onChange={(e) => handleInputChange('personalInfo', 'fullName', e.target.value)}
          error={!!errors['personalInfo.fullName']}
          helperText={errors['personalInfo.fullName']}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors['personalInfo.gender']}>
          <InputLabel>性别 *</InputLabel>
          <Select
            value={getSafeValue(data.personalInfo.gender)}
            onChange={(e) => handleInputChange('personalInfo', 'gender', e.target.value)}
            label="性别 *"
          >
            <MenuItem value="Male">男</MenuItem>
            <MenuItem value="Female">女</MenuItem>
          </Select>
          {errors['personalInfo.gender'] && (
            <FormHelperText>{errors['personalInfo.gender']}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="护照号码 *"
          value={getSafeValue(data.personalInfo.passportNumber)}
          onChange={(e) => handleInputChange('personalInfo', 'passportNumber', e.target.value)}
          error={!!errors['personalInfo.passportNumber']}
          helperText={errors['personalInfo.passportNumber']}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="出生日期 *"
          type="date"
          value={getSafeValue(data.personalInfo.dateOfBirth)}
          onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
          error={!!errors['personalInfo.dateOfBirth']}
          helperText={errors['personalInfo.dateOfBirth']}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="国籍 *"
          value={getSafeValue(data.personalInfo.nationality)}
          onChange={(e) => handleInputChange('personalInfo', 'nationality', e.target.value)}
          error={!!errors['personalInfo.nationality']}
          helperText={errors['personalInfo.nationality']}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="手机号码 *"
          value={getSafeValue(data.personalInfo.phone)}
          onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
          error={!!errors['personalInfo.phone']}
          helperText={errors['personalInfo.phone']}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="邮箱地址 *"
          type="email"
          value={getSafeValue(data.personalInfo.email)}
          onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
          error={!!errors['personalInfo.email']}
          helperText={errors['personalInfo.email']}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="身份证号码"
          value={getSafeValue(data.personalInfo.idCardNumber)}
          onChange={(e) => handleInputChange('personalInfo', 'idCardNumber', e.target.value)}
          
        />  
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>宗教信仰</InputLabel>
          <Select
            value={getSafeValue(data.personalInfo.religion)}
            onChange={(e) => handleInputChange('personalInfo', 'religion', e.target.value)}
            label="宗教信仰 *"
          >
            <MenuItem value="NO RELIGION">无宗教信仰</MenuItem>
            <MenuItem value="TAO">道教</MenuItem>
            <MenuItem value="CONFUCIANISM">儒教</MenuItem>
            <MenuItem value="BUDDHIST">佛教</MenuItem>
            <MenuItem value="BAHAI">巴哈伊教</MenuItem>
            <MenuItem value="JUDAISM">犹太教</MenuItem>
            <MenuItem value="HINDU">印度教</MenuItem>
            <MenuItem value="ISLAM">伊斯兰教</MenuItem>
            <MenuItem value="CHRISTIAN">基督教</MenuItem>
            <MenuItem value="SIKHISM">锡克教</MenuItem>
            <MenuItem value="OTHERS">其他</MenuItem>
          </Select>
          
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>婚姻状况</InputLabel>
          <Select
            value={getSafeValue(data.personalInfo.maritalStatus)}
            onChange={(e) => handleInputChange('personalInfo', 'maritalStatus', e.target.value)}
            label="婚姻状况 *"
          >
            <MenuItem value="Single">单身</MenuItem>
            <MenuItem value="Married">已婚</MenuItem>
            <MenuItem value="Widower">鳏夫</MenuItem>
            <MenuItem value="Widow">寡妇</MenuItem>
          </Select>
          
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          type="date"
          fullWidth
          label="护照签发日期"
          value={getSafeValue(data.personalInfo.passportIssueDate)}
          onChange={(e) => handleInputChange('personalInfo', 'passportIssueDate', e.target.value)}
          error={!!errors['personalInfo.passportIssueDate']}
          helperText={errors['personalInfo.passportIssueDate']}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          type="date"
          fullWidth
          label="护照到期日期"
          value={getSafeValue(data.personalInfo.passportExpiryDate)}
          onChange={(e) => handleInputChange('personalInfo', 'passportExpiryDate', e.target.value)}
          error={!!errors['personalInfo.passportExpiryDate']}
          helperText={errors['personalInfo.passportExpiryDate']}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="护照签发地点"
          value={getSafeValue(data.personalInfo.passportIssuePlace)}
          onChange={(e) => handleInputChange('personalInfo', 'passportIssuePlace', e.target.value)}
          error={!!errors['personalInfo.passportIssuePlace']}
          helperText={errors['personalInfo.passportIssuePlace']}
        />
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="身高(CM)"
          value={getSafeValue(data.personalInfo.bodyHeight)}
          onChange={(e) => handleInputChange('personalInfo', 'bodyHeight', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="体重(KG)"
          value={getSafeValue(data.personalInfo.bodyWeight)}
          onChange={(e) => handleInputChange('personalInfo', 'bodyWeight', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="月薪"
          value={getSafeValue(data.personalInfo.salary)}
          onChange={(e) => handleInputChange('personalInfo', 'salary', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="父亲姓名"
          value={getSafeValue(data.personalInfo.fatherName)}
          onChange={(e) => handleInputChange('personalInfo', 'fatherName', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="母亲姓名"
          value={getSafeValue(data.personalInfo.motherName)}
          onChange={(e) => handleInputChange('personalInfo', 'motherName', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>是否参加高考</InputLabel>
          <Select
            value={getSafeValue(data.personalInfo.ifCee)}
            onChange={(e) => handleInputChange('personalInfo', 'ifCee', e.target.value)}
            label="是否参加高考"
          >
            <MenuItem value="1">是</MenuItem>
            <MenuItem value="0">否</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="兄弟姐妹数量"
          value={getSafeValue(data.personalInfo.siblingsCount)}
          onChange={(e) => handleInputChange('personalInfo', 'siblingsCount', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="您在兄弟姐妹中的排行"
          value={getSafeValue(data.personalInfo.siblingsRanking)}
          onChange={(e) => handleInputChange('personalInfo', 'siblingsRanking', e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="通讯地址"
          multiline
          rows={2}
          value={getSafeValue(data.personalInfo.CorrespondenceAddress)}
          onChange={(e) => handleInputChange('personalInfo', 'CorrespondenceAddress', e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="永久居住地址"
          multiline
          rows={2}
          value={getSafeValue(data.personalInfo.PermanentAddress)}
          onChange={(e) => handleInputChange('personalInfo', 'PermanentAddress', e.target.value)}
        />
      </Grid>
    </Grid>
  );
  const renderEnglishLanguage = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors['englishLanguage.languageType']}>
          <InputLabel>英语语言能力 *</InputLabel>
          <Select
            value={getSafeValue(data.englishLanguage.languageType)}
            onChange={(e) => handleInputChange('englishLanguage', 'languageType', e.target.value)}
            label="英语语言能力 *"
          >
            <MenuItem value="IELTS">雅思</MenuItem>
            <MenuItem value="TOEFL">托福</MenuItem>
            <MenuItem value="Cambridge English Qualifications">剑桥英语</MenuItem>
            <MenuItem value="PTE">PTE</MenuItem>
            <MenuItem value="Linguaskill">领思</MenuItem>
            <MenuItem value="MUET">MUET</MenuItem>
            <MenuItem value="HEET">HEET</MenuItem>
            <MenuItem value="Duolingo English Test">多邻国</MenuItem>
            <MenuItem value="NOT">暂未取得</MenuItem>
            <MenuItem value="OTHERS">其他</MenuItem>
          </Select>
          {errors['englishLanguage.languageType'] && (
            <FormHelperText>{errors['englishLanguage.languageType']}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      {data.englishLanguage.languageType === 'OTHERS' && (
        <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <TextField
          fullWidth
          label="其他英语语言能力"
          value={getSafeValue(data.englishLanguage.languageCustomType)}
          onChange={(e) => handleInputChange('englishLanguage', 'languageCustomType', e.target.value)}
          error={!!errors['englishLanguage.languageCustomType']}
          helperText={errors['englishLanguage.languageCustomType']}
        />
        </FormControl>
      </Grid>
      )}
      {data.englishLanguage.languageType !== 'NOT' && (
        <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          
          <TextField
          fullWidth
          label="成绩"
          value={getSafeValue(data.englishLanguage.languageResult)}
          onChange={(e) => handleInputChange('englishLanguage', 'languageResult', e.target.value)}
          error={!!errors['englishLanguage.languageResult']}
          helperText={errors['englishLanguage.languageResult']}
        />
        </FormControl>
      </Grid>
      )}
      
    </Grid>
  )
  const renderEducation = () => (
    <Box>
      {Array.isArray(data.education) && data.education.map((edu, index) => (
        <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
              教育经历 {index + 1}
            </Typography>
            {data.education.length > 1 && (
              <IconButton
                onClick={() => handleDeleteEducation(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors[`education.${index}.currentEducationLevel`]}>
                <InputLabel>当前最高教育水平 *</InputLabel>
                <Select
                  value={getSafeValue(edu.currentEducationLevel)}
                  onChange={(e) => handleEducationChange(index, 'currentEducationLevel', e.target.value)}
                  label="当前最高教育水平 *"
                >
                  <MenuItem value="High School">普通高中</MenuItem>
                  <MenuItem value="Technical secondary school">职高（中专）</MenuItem>
                  <MenuItem value="International High School">国际高中</MenuItem>
                  <MenuItem value="Diploma">大专</MenuItem>
                  <MenuItem value="Bachelor">本科</MenuItem>
                  <MenuItem value="Master">硕士</MenuItem>
                </Select>
                {errors[`education.${index}.currentEducationLevel`] && (
                  <FormHelperText>{errors[`education.${index}.currentEducationLevel`]}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="学校名称 *"
                value={getSafeValue(edu.schoolName)}
                onChange={(e) => handleEducationChange(index, 'schoolName', e.target.value)}
                error={!!errors[`education.${index}.schoolName`]}
                helperText={errors[`education.${index}.schoolName`]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="专业"
                value={getSafeValue(edu.major)}
                onChange={(e) => handleEducationChange(index, 'major', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GPA/平均分"
                value={getSafeValue(edu.gpa)}
                onChange={(e) => handleEducationChange(index, 'gpa', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>学习模式</InputLabel>
                <Select
                  value={getSafeValue(edu.studyMode)}
                  onChange={(e) => handleEducationChange(index, 'studyMode', e.target.value)}
                  label="学习模式"
                >
                  <MenuItem value="Full-time">全日制</MenuItem>
                  <MenuItem value="Part-time">非全日制</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="入学时间 *"
                type="date"
                value={getSafeValue(edu.enrolledDate)}
                onChange={(e) => handleEducationChange(index, 'enrolledDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!errors[`education.${index}.enrolledDate`]}
                helperText={errors[`education.${index}.enrolledDate`]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="毕业时间 *"
                type="date"
                value={getSafeValue(edu.graduationDate)}
                onChange={(e) => handleEducationChange(index, 'graduationDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!errors[`education.${index}.graduationDate`]}
                helperText={errors[`education.${index}.graduationDate`]}
              />
            </Grid>
          </Grid>
        </Box>
      ))}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          onClick={handleAddEducation}
          startIcon={<SchoolIcon />}
        >
          添加教育经历
        </Button>
      </Box>
    </Box>
  );

  const renderWorkExperience = () => (
    <Box>
      {(Array.isArray(data.workExperience) && data.workExperience.length > 0)
        ? data.workExperience.map((exp, index) => (
          exp ? (
            <Card key={index} sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="公司名称"
                      fullWidth
                      value={getSafeValue(exp.companyName)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'companyName', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="职位"
                      fullWidth
                      value={getSafeValue(exp.position)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'position', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="date"
                      label="开始时间"
                      fullWidth
                      value={getSafeValue(exp.startDate)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'startDate', e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="date"
                      label="结束时间"
                      fullWidth
                      value={exp.isCurrent ? '' : getSafeValue(exp.endDate)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'endDate', e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      disabled={exp.isCurrent}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exp.isCurrent}
                          onChange={(e) =>
                            handleExperienceChange(index, 'isCurrent', e.target.checked)
                          }
                        />
                      }
                      label="至今"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="公司地址"
                      fullWidth
                      value={getSafeValue(exp.address)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'address', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="邮政编码"
                      fullWidth
                      value={getSafeValue(exp.postalCode)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'postalCode', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="联系电话"
                      fullWidth
                      value={getSafeValue(exp.phone)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'phone', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>工作类型</InputLabel>
                      <Select
                        value={getSafeValue(exp.workType)}
                        label="工作类型"
                        onChange={(e) =>
                          handleExperienceChange(index, 'workType', e.target.value)
                        }
                      >
                        <MenuItem value="gov">政府机/法定机构</MenuItem>
                        <MenuItem value="self">自雇人士</MenuItem>
                        <MenuItem value="private">私人单位</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="工作内容"
                      fullWidth
                      multiline
                      rows={3}
                      value={getSafeValue(exp.description)}
                      onChange={(e) =>
                        handleExperienceChange(index, 'description', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: 'right' }}>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteExperience(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : null
        ))
        : null}
      <Button variant="outlined" onClick={handleAddExperience}>
        添加工作经历
      </Button>
    </Box>
  );
  

  const renderEmergencyContact = () => (
    <Box>
      {(Array.isArray(data.emergencyContact) && data.emergencyContact.length > 0)
        ? data.emergencyContact.map((contact, index) => (
          contact ? (
            <Card key={index} sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="姓名"
                      fullWidth
                      value={contact.name || ''}
                      onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="出生日期"
                      type="date"
                      fullWidth
                      value={contact.birthDate || ''}
                      onChange={(e) => handleContactChange(index, 'birthDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="与申请人关系"
                      fullWidth
                      value={contact.relation || ''}
                      onChange={(e) => handleContactChange(index, 'relation', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="工作单位名称"
                      fullWidth
                      value={contact.companyName || ''}
                      onChange={(e) => handleContactChange(index, 'companyName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="职位"
                      fullWidth
                      value={contact.position || ''}
                      onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="联系地址"
                      fullWidth
                      value={contact.address || ''}
                      onChange={(e) => handleContactChange(index, 'address', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="邮政编码"
                      fullWidth
                      value={contact.postalCode || ''}
                      onChange={(e) => handleContactChange(index, 'postalCode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="家庭电话"
                      fullWidth
                      value={contact.homePhone || ''}
                      onChange={(e) => handleContactChange(index, 'homePhone', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="单位电话"
                      fullWidth
                      value={contact.companyPhone || ''}
                      onChange={(e) => handleContactChange(index, 'companyPhone', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="手机号码"
                      fullWidth
                      value={contact.mobilePhone || ''}
                      onChange={(e) => handleContactChange(index, 'mobilePhone', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="月薪(人民币)"
                      fullWidth
                      value={contact.salary || ''}
                      onChange={(e) => handleContactChange(index, 'salary', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="身份证号"
                      fullWidth
                      value={contact.idNumber || ''}
                      onChange={(e) => handleContactChange(index, 'idNumber', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="护照号"
                      fullWidth
                      value={contact.passportNumber || ''}
                      onChange={(e) => handleContactChange(index, 'passportNumber', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: 'right' }}>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteContact(index)}
                      title={data.emergencyContact.length === 1 ? '至少保留一个联系人' : '删除联系人'}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : null
        ))
        : null}
      <Button
        variant="outlined"
        onClick={handleAddContact}
        disabled={data.emergencyContact.length >= 2}
      >
        添加联系人
      </Button>
    </Box>
  );

  const renderReferee = () => (
    <Box>
      {(Array.isArray(data.referee) && data.referee.length > 0)
        ? data.referee.map((ref, index) => (
          ref ? (
            <Card key={index} sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="姓名"
                      fullWidth
                      value={ref.name || ''}
                      onChange={(e) => handleRefereeChange(index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="手机号码"
                      fullWidth
                      value={ref.phone || ''}
                      onChange={(e) => handleRefereeChange(index, 'phone', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="邮箱"
                      fullWidth
                      value={ref.email || ''}
                      onChange={(e) => handleRefereeChange(index, 'email', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="工作单位名称"
                      fullWidth
                      value={ref.companyName || ''}
                      onChange={(e) => handleRefereeChange(index, 'companyName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="职位"
                      fullWidth
                      value={ref.position || ''}
                      onChange={(e) => handleRefereeChange(index, 'position', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="公司地址"
                      fullWidth
                      value={ref.companyAddress || ''}
                      onChange={(e) => handleRefereeChange(index, 'companyAddress', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="邮政编码"
                      fullWidth
                      value={ref.postalCode || ''}
                      onChange={(e) => handleRefereeChange(index, 'postalCode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: 'right' }}>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteReferee(index)}
                      title={data.referee.length === 1 ? '至少保留一个推荐人' : '删除推荐人'}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : null
        ))
        : null}
      <Button
        variant="outlined"
        onClick={handleAddReferee}
        disabled={data.referee.length >= 2}
      >
        添加推荐人
      </Button>
    </Box>
  );
  

  const renderElse = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors['additionalInformation.preferredStartDate']}>
          <InputLabel>期望入学时间 *</InputLabel>
          <Select
            value={data.additionalInformation.preferredStartDate || ''}
            onChange={(e) => handleInputChange('additionalInformation', 'preferredStartDate', e.target.value)}
            label="期望入学时间 *"
          >
            
            
            <MenuItem value="2025-09">2025年9月</MenuItem>
            <MenuItem value="2026-01">2026年1月</MenuItem>
            <MenuItem value="2026-01">2026年9月</MenuItem>
          </Select>
          {errors['additionalInformation.preferredStartDate'] && (
            <FormHelperText>{errors['additionalInformation.preferredStartDate']}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors['additionalInformation.studyBudget']}>
          <InputLabel>学习预算 *</InputLabel>
          <Select
            value={data.additionalInformation.studyBudget || ''}
            onChange={(e) => handleInputChange('additionalInformation', 'studyBudget', e.target.value)}
            label="学习预算 *"
          >
            <MenuItem value="50000">5万以下</MenuItem>
            <MenuItem value="50000-100000">5-10万</MenuItem>
            <MenuItem value="100000-200000">10-20万</MenuItem>
            <MenuItem value="200000">20万以上</MenuItem>
          </Select>
          {errors['additionalInformation.studyBudget'] && (
            <FormHelperText>{errors['additionalInformation.studyBudget']}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">住宿需求</FormLabel>
          <RadioGroup
            row
            value={data.additionalInformation.accommodationNeeds || ''}
            onChange={(e) => handleInputChange('additionalInformation', 'accommodationNeeds', e.target.value)}
          >
            <FormControlLabel value="dormitory" control={<Radio />} label="学校宿舍" />
            <FormControlLabel value="homestay" control={<Radio />} label="寄宿家庭" />
            <FormControlLabel value="apartment" control={<Radio />} label="独立公寓" />
            <FormControlLabel value="no_need" control={<Radio />} label="不需要" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">是否有签证申请或拒签历史？</FormLabel>
          <RadioGroup
            row
            value={data.additionalInformation.visaHistory || ''}
            onChange={(e) => handleInputChange('additionalInformation', 'visaHistory', e.target.value)}
          >
            <FormControlLabel value="none" control={<Radio />} label="无" />
            <FormControlLabel value="approved" control={<Radio />} label="有，已获签" />
            <FormControlLabel value="rejected" control={<Radio />} label="有，被拒签" />
          </RadioGroup>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="其他补充信息"
          multiline
          rows={3}
          value={data.additionalInformation.additionalInfo || ''}
          onChange={(e) => handleInputChange('additionalInformation', 'additionalInfo', e.target.value)}
          placeholder="其他您认为需要补充的信息"
        />
      </Grid>
    </Grid>
  );

  // 个人信息字段值映射
  const personalInfoValueMap = {
    gender: {
      Male: '男',
      Female: '女',
    },
    maritalStatus: {
      Single: '单身',
      Married: '已婚',
      Widower: '鳏夫',
      Widow: '寡妇',
    },
    religion: {
      'NO RELIGION': '无宗教信仰',
      TAO: '道教',
      CONFUCIANISM: '儒教',
      BUDDHIST: '佛教',
      BAHAI: '巴哈伊教',
      JUDAISM: '犹太教',
      HINDU: '印度教',
      ISLAM: '伊斯兰教',
      CHRISTIAN: '基督教',
      SIKHISM: '锡克教',
      OTHERS: '其他',
    },
    ifCee: {
      '1': '是',
      '0': '否',
    },
  };

  // 新增：其他信息字段值映射
  const additionalInfoValueMap = {
    accommodationNeeds: {
      dormitory: '学校宿舍',
      homestay: '寄宿家庭',
      apartment: '独立公寓',
      no_need: '不需要',
    },
    studyBudget: {
      '50k': '5万以下',
      '50k-100k': '5-10万',
      '100k-200k': '10-20万',
      '200k': '20万以上',
    },
    visaHistory: {
      none: '无',
      approved: '有，已获签',
      rejected: '有，被拒签',
    },
  };

  // 个人信息值映射函数
  const getPersonalInfoDisplayValue = (key, value) => {
    if (!value) return '—';
    if (personalInfoValueMap[key] && value in personalInfoValueMap[key]) {
      return personalInfoValueMap[key][value];
    }
    return value;
  };

  // 新增：其他信息值映射函数
  const getAdditionalInfoDisplayValue = (key, value) => {
    if (!value) return '—';
    if (additionalInfoValueMap[key] && value in additionalInfoValueMap[key]) {
      return additionalInfoValueMap[key][value];
    }
    return value;
  };

  // 教育程度值映射
  const educationLevelMap = {
    'High School': '普通高中',
    'Technical secondary school': '职高（中专）',
    'International High School': '国际高中',
    'Diploma': '大专',
    'Bachelor': '本科',
    'Master': '硕士',
  };

  // 学习模式值映射
  const studyModeMap = {
    'Full-time': '全日制',
    'Part-time': '非全日制',
  };

  const getEducationDisplayValue = (key, value) => {
    if (!value) return '—';
    if (key === 'currentEducationLevel' && value in educationLevelMap) {
      return educationLevelMap[value];
    }
    if (key === 'studyMode' && value in studyModeMap) {
      return studyModeMap[value];
    }
    return value;
  };

  // 工作类型值映射
  const workTypeMap = {
    gov: '政府机/法定机构',
    self: '自雇人士',
    private: '私人单位',
  };

  const getWorkDisplayValue = (key, value) => {
    if (!value) return '—';
    if (key === 'workType' && value in workTypeMap) {
      return workTypeMap[value];
    }
    if (key === 'isCurrent') {
      return value === true || value === 'true' ? '是' : '否';
    }
    return value;
  };

  const renderConfirmation = () => (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: 3, mb: 3 }}>
      {/* 个人信息 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>个人信息</Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {Object.entries(data.personalInfo).map(([key, value]) => (
          <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
            <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>{getPersonalInfoDisplayValue(key, value)}</Box>
          </Typography>
        ))}
      </Box>
      <Divider sx={{ my: 2 }} />
      {/* 英语语言能力 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>英语语言能力</Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {/* 语言类型特殊处理 */}
        <Typography sx={{ mb: 0.5, fontSize: 14 }}>
          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels.languageType || '语言考试类型'}：</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {data.englishLanguage.languageType === 'OTHERS'
              ? (data.englishLanguage.languageCustomType ? `其他（${data.englishLanguage.languageCustomType}）` : '其他')
              : (languageTypeMap[data.englishLanguage.languageType] || data.englishLanguage.languageType)}
          </Box>
        </Typography>
        {/* 语言成绩 */}
        <Typography sx={{ mb: 0.5, fontSize: 14 }}>
          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels.languageResult || '语言成绩'}：</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>{data.englishLanguage.languageResult || '—'}</Box>
        </Typography>
      </Box>
      <Divider sx={{ my: 2 }} />
      {/* 教育背景 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>教育背景</Typography>
      {Array.isArray(data.education) && data.education.length === 0 ? (
        <Typography color="text.secondary">无教育经历</Typography>
      ) : (Array.isArray(data.education) ? data.education.map((item, idx) => (
        item ? (
          <Box key={idx} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              教育经历 {idx + 1}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{getEducationDisplayValue(key, value)}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      )) : null)}
      <Divider sx={{ my: 2 }} />
      {/* 工作经历 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>工作经历</Typography>
      {Array.isArray(data.workExperience) && data.workExperience.length === 0 ? (
        <Typography color="text.secondary">无工作经历</Typography>
      ) : (Array.isArray(data.workExperience) ? data.workExperience.map((item, idx) => (
        item ? (
          <Box key={idx} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              工作经历 {idx + 1}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{getWorkDisplayValue(key, value)}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      )) : null)}
      <Divider sx={{ my: 2 }} />
      {/* 联系人 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>联系人信息</Typography>
      {Array.isArray(data.emergencyContact) && data.emergencyContact.length === 0 ? (
        <Typography color="text.secondary">无联系人</Typography>
      ) : (Array.isArray(data.emergencyContact) ? data.emergencyContact.map((item, idx) => (
        item ? (
          <Box key={idx} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              紧急联系人 {idx + 1}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{value || '—'}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      )) : null)}
      <Divider sx={{ my: 2 }} />
      {/* 推荐人 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>推荐人信息</Typography>
      {Array.isArray(data.referee) && data.referee.length === 0 ? (
        <Typography color="text.secondary">无推荐人</Typography>
      ) : (Array.isArray(data.referee) ? data.referee.map((item, idx) => (
        item ? (
          <Box key={idx} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              推荐人 {idx + 1}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{value || '—'}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      )) : null)}
      <Divider sx={{ my: 2 }} />
      {/* 其他信息 */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>其他信息</Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {Object.entries(data.additionalInformation || {}).map(([key, value]) => (
          <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
            <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>{getAdditionalInfoDisplayValue(key, value)}</Box>
          </Typography>
        ))}
      </Box>
    </Box>
  );

  // 语言类型映射
  const languageTypeMap = {
    IELTS: '雅思',
    TOEFL: '托福',
    'Cambridge English Qualifications': '剑桥英语',
    PTE: 'PTE',
    Linguaskill: '领思',
    MUET: 'MUET',
    HEET: 'HEET',
    'Duolingo English Test': '多邻国',
    NOT: '暂未取得',
    OTHERS: '其他',
  };

  if (!isAuthenticated) {
    if (onRequireLogin) onRequireLogin();
    return null;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        第三步：填写申请信息
      </Typography>
      
      {/* 开发模式：模拟数据切换按钮 */}
      {/* {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            开发模式 - 模拟数据
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={toggleMockData}
            sx={{ mr: 1 }}
          >
            {useMockData ? '清空数据' : '填充模拟数据'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            当前状态: {useMockData ? '使用模拟数据' : '空数据'}
          </Typography>
        </Box>
      )} */}

      {/* 表单步骤指示器 */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {formSteps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mx: 1,
              }}
            >
              <Chip
                icon={step.icon}
                label={step.title}
                variant={currentFormStep === index ? "filled" : "outlined"}
                color={currentFormStep === index ? "primary" : "default"}
                sx={{
                  fontWeight: currentFormStep === index ? 600 : 400,
                }}
              />
              {index < formSteps.length - 1 && (
                <Typography sx={{ mx: 1, color: 'text.secondary' }}>
                  →
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary' 
          }}
        >
          {formSteps[currentFormStep].description}
        </Typography>
      </Box>

      {/* 表单内容 */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          {currentFormStep === 0 && renderPersonalInfo()}
          {currentFormStep === 1 && renderEnglishLanguage()}
          {currentFormStep === 2 && renderEducation()}
          {currentFormStep === 3 && renderWorkExperience()}
          {currentFormStep === 4 && renderEmergencyContact()}
          {currentFormStep === 5 && renderReferee()}
          {currentFormStep === 6 && renderElse()}
          {currentFormStep === 7 && renderConfirmation()}
        </CardContent>
      </Card>

      {/* 导航按钮 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleFormBack}
          disabled={loading}
        >
          ← {currentFormStep === 0 ? '返回选择套餐' : '上一步'}
        </Button>
        <Button
          variant="contained"
          onClick={handleFormNext}
          disabled={loading}
        >
          {currentFormStep === formSteps.length - 1 ? (loading ? '提交中...' : '确认信息') : '下一项'} →
        </Button>
      </Box>
      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>
      )}
      <MultiLoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};

export default ApplicationForm;
